import { useCallback, useEffect, useMemo, useState } from 'react';
import { ItemCountingLineDTO, ProductDetailDTO } from '../../../api/api';
import styled from 'styled-components';
import Button from '../../../components/button/Button';
import Link from '../../../components/link/Link';
import Typography from '../../../components/typography/Typography';
import GoodsService from '../../../services/GoodsService';
import NotificationService from '../../../services/NotificationService';
import { log } from '../../../utils/logging/log';
import NavigationHeader from '../components/NavigationHeader';
import LoadingOverlay from '../../../components/loading-overlay/LoadingOverlay';
import CountGoodsList from '../components/CountGoodsList';
import { StyledTable } from '../components/Styles';
import { useGoodsList } from '../order-goods-dialog/goods-list/useGoodsList';
import ItemCountingService from '../../../services/ItemCountingService';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import { DialogBody } from '../../../stateManagement/reducers/confirmDialogReducer';

interface Props {
  onClose: () => void;
}

const CountGoodsViewContent = (props: Props) => {
  const { onClose } = props;

  const [page, setPage] = useState<'count' | 'add' | 'processing'>('count');

  const [allProducts, setAllProducts] = useState<ProductDetailDTO[]>([]);
  const [itemCounting, setItemCounting] = useState<ItemCountingLineDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const { getConfirmation } = useConfirmationDialog();

  const getItemCounting = async () => {
    ItemCountingService.getItemCounting()
      .then((data) => {
        data.itemCountingLines && setItemCounting(data.itemCountingLines);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Kunne ikke hente vareliste');
      });
  };

  useEffect(() => {
    setLoading(true);
    getItemCounting().finally(() => {
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    GoodsService.getAllProducts().then((response) => {
      setAllProducts(response);
    });
  }, []);

  const addGood = useCallback((goodsId?: string, goodsName?: string, amount?: number) => {
    const newItem = {
      itemId: goodsId,
      quantity: amount,
      checked: false,
      itemName: goodsName
    };
    ItemCountingService.addItemCountingLine(newItem)
      .then(() => {
        setItemCounting((prev) => [...prev, newItem]);
        NotificationService.success(`${goodsName} blev tilføjet til vareoptællingen.`);
      })
      .catch((error) => {
        log(error);
        NotificationService.error('Der skete en fejl. Kunne ikke tilføje vare');
      });
  }, []);

  const updateGoodQuantity = (itemId?: string, quantity?: number, checked?: boolean) => {
    setLoading(true);
    const updatedGood = {
      quantity,
      itemId,
      checked
    } as ItemCountingLineDTO;
    ItemCountingService.updateItemCountingLine(updatedGood)
      .then(() => {
        setItemCounting(
          itemCounting.map((g) => {
            if (g.itemId === itemId) {
              return { ...g, ...updatedGood };
            } else {
              return g;
            }
          })
        );
      })
      .catch((error) => {
        NotificationService.error('Kunne ikke opdatere vareoptælling');
        log(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const submit = () => {
    setLoading(true);
    ItemCountingService.submitItemCounting()
      .then(() => {
        NotificationService.success('Optællingen færdig');
      })
      .catch((error) => {
        log(error);
        NotificationService.success('Kunne ikke registrer optællingen');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reset = async () => {
    const confirmation = await getConfirmation({
      headerText: 'Nulstil optælling',
      bodyText: 'Er du sikker på, at du vil nulstille optællingen? Handlingen kan ikke fortrydes.',
      confirmButtonText: 'Bekræft',
      declineButtonText: 'Fortryd'
    } as DialogBody);

    if (confirmation === 'confirm') {
      setLoading(true);
      ItemCountingService.deleteItemCounting()
        .then(() => {
          getItemCounting().then(() => {
            NotificationService.success('Optællingen blev nulstillet');
          });
        })
        .catch((e) => {
          log(e);
          NotificationService.error('Der skete en fejl. Kunne ikke nulstille optællingen');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const filteredAllProducts = useMemo(() => {
    return allProducts.filter((good) => !itemCounting.some((obj) => obj.itemId === good.itemId));
  }, [allProducts, itemCounting]);

  const { tableInstance } = useGoodsList(filteredAllProducts, addGood);

  return (
    <Container>
      {loading && <LoadingOverlay />}
      <NavigationHeader
        headerTitle={page === 'add' ? 'Tilføj varetype til bilens lager' : 'Lav optælling'}
        onBackButton={page === 'add' ? () => setPage('count') : undefined}
        onClose={onClose}
      />

      <ContentContainer>
        {page === 'count' && (
          <CountGoodsList
            goods={itemCounting.filter((good) => itemCounting.some((obj) => obj.itemId !== good.itemId))}
            updateGoodQuantity={(itemId, quantity, checked) => updateGoodQuantity(itemId, quantity, checked)}
            reset={reset}
          />
        )}
        {page === 'add' && <StyledTable tableInstance={tableInstance} loading={loading} alwaysShowSort showPagination />}

        {page === 'processing' && (
          <Processing>
            <Typography variant="h1"> Vareoptællingen er sendt til D365</Typography>
            <Typography variant="h4"> Det kan tage noget tid før D365 opdaterer din beholdning i Work.</Typography>
            <CloseButton onClick={onClose}>Luk vindue</CloseButton>
          </Processing>
        )}
      </ContentContainer>
      {
        <Footer>
          {page === 'count' && (
            <>
              <TextBoks onClick={() => setPage('add')}>
                <Typography variant="h6">
                  Har du varetyper der ikke er på listen?
                  <StyledLink
                    onClick={() => {
                      setPage('add');
                    }}
                    minWidth="80px"
                  >
                    Slå dem op her
                  </StyledLink>
                </Typography>
              </TextBoks>
              <StyledButton disabled={itemCounting.some((item) => !item.checked)} onClick={submit}>
                Registrer varer
              </StyledButton>
            </>
          )}
        </Footer>
      }
    </Container>
  );
};

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  background-color: white;
  height: 100%;
  width: 100%;
`;

const ContentContainer = styled.div`
  background: white;
  width: calc(100% - 50px);
  height: calc(100% - 182px);
  padding-top: 10px;
`;

const Footer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${(props) => props.theme.palette.grey.black5};
  width: 100%;
  height: 100px;
  bottom: 0;
`;

const StyledButton = styled(Button)`
  margin: 0px 20px;
`;

const TextBoks = styled.div`
  display: flex;
  padding-left: 30px;
`;

const StyledLink = styled(Link)`
  display: inline-block;
  position: relative;
  z-index: ${(props) => props.theme.zIndex.main};
  padding: 1em;
  margin: -1em;
`;

const Processing = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CloseButton = styled(Button)`
  margin-top: ${(props) => props.theme.spacing(15)};
`;

export default CountGoodsViewContent;
